/**
 * Utility for resolving Wiki source from hierarchy chain
 *
 * The logic: traverse from the deepest level up until we find a KNOWN source.
 * Only known sources are valid for API calls.
 */

/**
 * Known source mappings - ONLY these sources are valid for API
 * - equipment -> armors
 * - weapon -> weapons
 * - items -> items
 * - npc -> npcs
 * - classes -> skills
 */
const SOURCE_TO_API_MAP: Record<string, string> = {
  equipment: 'armors',
  weapon: 'weapons',
  items: 'items',
  npc: 'npcs',
  classes: 'skills',
  skills: 'skills',
  accessories: 'accessories',
  recipes: 'recipes',
  bijouterie: 'jewelry'
};

/**
 * Resolve API endpoint from a chain of source values
 * Traverses from the end (deepest level) to the beginning (top level)
 * Returns the FIRST KNOWN source (mapped to API endpoint)
 *
 * @param sourceChain - Array of source values from hierarchy (top to bottom)
 *                      e.g., [category.source, subCategory.source, type.source, subType.source]
 * @returns API endpoint string
 *
 * @example
 * // Chain: [items, recipes, undefined] → 'items' (recipes is not known, items is)
 * resolveApiSource(['items', 'recipes', undefined]) // returns 'items'
 *
 * @example
 * // Chain: [equipment, weapon, undefined] → 'weapons' (weapon is known, mapped)
 * resolveApiSource(['equipment', 'weapon', undefined]) // returns 'weapons'
 */
export function resolveApiSource(sourceChain: (string | undefined | null)[]): string {
  // Traverse from the end (deepest level) to the beginning (top level)
  // Return the first KNOWN source (mapped to API endpoint)
  for (let i = sourceChain.length - 1; i >= 0; i--) {
    const source = sourceChain[i];
    if (source && SOURCE_TO_API_MAP[source]) {
      return SOURCE_TO_API_MAP[source];
    }
  }

  return 'items'; // Ultimate fallback
}

/**
 * Get API endpoint from a single source value
 * Returns mapped value if source is known, otherwise returns 'items' fallback
 *
 * @param source - Source value
 * @returns API endpoint string
 */
export function getApiEndpoint(source: string): string {
  return SOURCE_TO_API_MAP[source] || 'items';
}

/**
 * Check if a source value is a known source that maps to API endpoint
 *
 * @param source - Source value to check
 * @returns true if source is known
 */
export function isKnownSource(source: string | undefined | null): boolean {
  return !!source && source in SOURCE_TO_API_MAP;
}
